import React, { useState } from "react";
import { CHART_1, INK, MUTED, GRID, PAPER } from "./palette";

// ---- prior_posterior: δ's prior and posterior on one axis, with the Savage–Dickey heights at 0 ----
// Props from ChartData#prior_posterior: delta, prior, posterior (densities on an even δ grid),
// scale (the Cauchy prior's r), priorAtZero, posteriorAtZero, bf01 (their ratio), median and
// interval [lo, hi] (the page's posterior median and 95% credible interval).
// Tokens, axes and tooltips follow ConceptChart.jsx: gray is the prior, teal the posterior being taught.
const W = 640, TOP = 16, RIGHT = 16;

const fmt = (x, places = 2) => (x < 0 ? "−" : "") + Math.abs(x).toFixed(places);
const minus = (t) => (t < 0 ? `−${-t}` : t);
const Halo = ({ children, ...props }) => <text fontSize="11" stroke={PAPER} strokeWidth="3" paintOrder="stroke" strokeLinejoin="round" {...props}>{children}</text>;

function ticks(min, max, step) {
	const out = [];
	for (let t = Math.ceil(min / step - 1e-9) * step; t <= max + 1e-9; t += step) out.push(+t.toFixed(10));
	return out;
}

function Tooltip({ x, lines }) {
	const longest = Math.max(...lines.map((l) => String(l).length));
	const width = Math.max(170, Math.round(longest * 6.4) + 16), left = Math.min(Math.max(x + 8, 16), W - RIGHT - width);
	return (
		<g pointerEvents="none" transform={`translate(${left},${TOP})`}>
			<rect width={width} height={10 + lines.length * 15} rx="3" fill={PAPER} stroke={GRID} />
			{lines.map((line, i) => (
				<text key={i} x="8" y={17 + i * 15} fontSize="11" fill={i === 0 ? MUTED : INK} fontWeight={i === 0 ? 400 : 600}>{line}</text>
			))}
		</g>
	);
}

export default function PriorPosteriorChart({ delta = [], prior = [], posterior = [], scale, priorAtZero, posteriorAtZero, bf01, median, interval = [] }) {
	const [hover, setHover] = useState(null);
	if (delta.length < 2 || interval.length !== 2) return null;
	const height = 270, box = { l: 40, r: W - RIGHT, t: TOP + 26, b: height - 40 };
	const lo = delta[0], hi = delta[delta.length - 1];
	const top = Math.max(...posterior, ...prior);
	const yStep = top > 3 ? 1 : 0.5, yMax = Math.ceil((top * 1.08) / yStep) * yStep;
	const x = (v) => box.l + ((v - lo) / (hi - lo)) * (box.r - box.l);
	const y = (d) => box.b - (d / yMax) * (box.b - box.t);
	const line = (ds) => ds.map((d, i) => `${i ? "L" : "M"}${x(delta[i]).toFixed(1)},${y(d).toFixed(1)}`).join(" ");
	const [ciLo, ciHi] = interval;
	const inside = delta.map((v, i) => [v, posterior[i]]).filter(([v]) => v >= ciLo && v <= ciHi);
	// Linear interpolation of the posterior between grid points, for the interval's edges and the median.
	const at = (v) => {
		const i = Math.min(Math.max(1, delta.findIndex((d) => d >= v)), delta.length - 1);
		const f = (v - delta[i - 1]) / (delta[i] - delta[i - 1]);
		return posterior[i - 1] + f * (posterior[i] - posterior[i - 1]);
	};
	const band = [[ciLo, at(ciLo)], ...inside, [ciHi, at(ciHi)]];
	const bandPath = `M${x(ciLo)},${box.b} ` + band.map(([v, d]) => `L${x(v).toFixed(1)},${y(d).toFixed(1)}`).join(" ") + ` L${x(ciHi)},${box.b} Z`;
	const r = fmt(scale, 3), ratio = `${fmt(posteriorAtZero)} ÷ ${fmt(priorAtZero)} = ${fmt(bf01)}`;
	const onMove = (event) => {
		const rect = event.currentTarget.getBoundingClientRect();
		const v = lo + ((((event.clientX - rect.left) / rect.width) * W - box.l) / (box.r - box.l)) * (hi - lo);
		if (v < lo || v > hi) return setHover(null);
		const i = Math.round(((v - lo) / (hi - lo)) * (delta.length - 1));
		setHover(i);
	};
	return (
		<figure>
			<div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600">
				<span className="flex items-center gap-1.5"><span className="inline-block w-4 border-t-2 border-dashed" style={{ borderColor: MUTED }} />Prior for δ: Cauchy, r = {r}</span>
				<span className="flex items-center gap-1.5"><span className="inline-block h-0.5 w-4 rounded" style={{ background: CHART_1 }} />Posterior for δ, if there is a difference</span>
				<span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: CHART_1, opacity: 0.2 }} />95% credible interval [{fmt(ciLo)}, {fmt(ciHi)}]</span>
			</div>
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img" onMouseMove={onMove} onMouseLeave={() => setHover(null)}
				aria-label={`The Cauchy prior for δ, the standardized difference, with scale r = ${r}, and δ's posterior if there is a difference. The posterior's median is ${fmt(median)} and its 95% credible interval runs from ${fmt(ciLo)} to ${fmt(ciHi)}. At δ = 0 the posterior is ${fmt(posteriorAtZero)} high and the prior ${fmt(priorAtZero)}: ${ratio}, the Bayes factor BF₀₁.`}>
				{ticks(0, yMax, yStep).map((t) => (
					<g key={`y${t}`}>
						<line x1={box.l} x2={box.r} y1={y(t)} y2={y(t)} stroke={GRID} />
						<text x={box.l - 6} y={y(t)} fontSize="11" fill={MUTED} textAnchor="end" dominantBaseline="middle">{t}</text>
					</g>
				))}
				{ticks(lo, hi, 0.5).map((t) => <text key={`x${t}`} x={x(t)} y={box.b + 16} fontSize="11" fill={MUTED} textAnchor="middle">{minus(t)}</text>)}
				<text x={box.r} y={box.b + 32} fontSize="11" fill={MUTED} textAnchor="end">δ, the difference between the population means in SDs</text>
				<text x={box.l} y={box.t - 10} fontSize="11" fill={MUTED}>Density (height)</text>
				<path d={bandPath} fill={CHART_1} opacity="0.15" />
				<line x1={x(0)} x2={x(0)} y1={box.t} y2={box.b} stroke={MUTED} strokeDasharray="4 3" opacity="0.6" />
				<path d={line(prior)} fill="none" stroke={MUTED} strokeWidth="2" strokeDasharray="6 4" />
				<path d={line(posterior)} fill="none" stroke={CHART_1} strokeWidth="2" strokeLinejoin="round" />
				<line x1={x(median)} x2={x(median)} y1={box.b} y2={y(at(median))} stroke={CHART_1} strokeWidth="1.5" />
				<Halo x={x(median) + 6} y={y(at(median)) - 6} fill={INK} fontWeight="600">Median {fmt(median)}</Halo>
				{/* The Savage–Dickey heights at δ = 0. */}
				<circle cx={x(0)} cy={y(posteriorAtZero)} r="4.5" fill={CHART_1} stroke={PAPER} strokeWidth="2" />
				<circle cx={x(0)} cy={y(priorAtZero)} r="4.5" fill={MUTED} stroke={PAPER} strokeWidth="2" />
				<Halo x={x(0) - 9} y={y(posteriorAtZero) + 4} textAnchor="end" fill={INK} fontWeight="600">Posterior at 0: {fmt(posteriorAtZero)}</Halo>
				<Halo x={x(0) - 9} y={y(priorAtZero) - 8} textAnchor="end" fill={INK} fontWeight="600">Prior at 0: {fmt(priorAtZero)}</Halo>
				<text x={box.l + 6} y={box.t + 6} fontSize="12" fill={INK} fontWeight="600">{ratio} = BF₀₁</text>
				<text x={box.l + 6} y={box.t + 21} fontSize="11" fill={MUTED}>the heights at δ = 0, posterior over prior</text>
				{hover !== null && (
					<g pointerEvents="none">
						<line x1={x(delta[hover])} x2={x(delta[hover])} y1={box.t} y2={box.b} stroke={MUTED} strokeWidth="1" />
						<Tooltip x={x(delta[hover])} lines={[`δ = ${fmt(delta[hover])}`, `Prior density ${prior[hover].toFixed(3)}`, `Posterior density ${posterior[hover].toFixed(3)}`]} />
					</g>
				)}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				The dashed curve is the default prior: before the data, it spreads its bets over small and large differences in either direction.
				The solid curve is δ's posterior, assuming there is a difference: the data pulled it in around small values, with its median at {fmt(median)} and its middle 95% from {fmt(ciLo)} to {fmt(ciHi)} (shaded).
				At δ = 0 the posterior is {fmt(posteriorAtZero)} high and the prior {fmt(priorAtZero)}, so the data made values near zero {fmt(bf01)} times more plausible than the prior had them. That ratio is the Bayes factor BF₀₁ (the Savage–Dickey density ratio).
			</figcaption>
		</figure>
	);
}
